import { Fragment } from 'react';
import { View } from '@react-pdf/renderer';
import FloorPlan from 'components/sections/FloorPlan';
import Canvas from 'components/sections/Canvas';
import Scale from 'components/sections/Scale';
import devicePlots from 'globalConstants/devicePlots';
import { gradients } from 'utilities/color';
import { get, round, toString, values } from 'lodash';
import { toFeet, toMeter } from 'utilities/format';

/**
* @function Plot
* @description Renders complete plot visualizations for PDF reports, combining
* heatmap overlays, floor plans, and scale legends.
* 
* @param {Object} props
* @param {string} [props.areaId] - Specific area ID to render, if any
* @param {Object} props.content - Room and device configuration data
* @param {string} props.device - Device type to plot
* @param {Object} props.constants - Application constants
* @param {Object} props.constants.defaultPlots - Default plot types by device
* 
* @description
* Orchestrates plot generation by:
* 1. Calculating plot dimensions and scale:
*    - Determines container vs area-specific view
*    - Scales plot to standard 532pt width
*    - Maintains aspect ratio
* 
* 2. Processing plot properties:
*    - Retrieves base properties from devicePlots
*    - Calculates min/max levels from data
*    - Handles units conversion (meters/feet)
*    - Computes averages and deviations
* 
* 3. Composing visualization layers:
*    - Generates color gradient mapping
*    - Renders heatmap canvas overlay
*    - Adds floor plan elements
*    - Includes scale legend
* 
* @example
* <Plot
*   device="speakers"
*   content={{
*     dimensions: {length: 100, width: 80},
*     areas: {
*       room1: {
*         dimensions: {length: 50, width: 40},
*         speakerLayout: [...]
*       }
*     },
*     isMeter: true
*   }}
*   constants={{
*     defaultPlots: {
*       speakers: "directSPL"
*     }
*   }}
* />
* 
* @note
* - Uses @react-pdf/renderer components
* - Maintains 532pt standard width
* - Supports both metric and imperial units
* - Calculates statistics across areas
* - Handles missing or invalid data gracefully
* - Combines multiple visualization layers
* - Passes props through to child components
*/
const Plot = (props) => {
  const { areaId = null, content, device, constants: { defaultPlots } } = props
  // Make the container 
  const inContainer = areaId ? false : true;
  const areas = content.areas;
  const dimensions = content.dimensions ?? areas[areaId].dimensions;
  const scale = 532 / Math.max(dimensions.length, dimensions.width);
  const bbox = { 
    width: ((inContainer ? dimensions.length : areas?.[areaId].dimensions.length) * scale) + 2, 
    height: ((inContainer ? dimensions.width : areas?.[areaId].dimensions.width) * scale) + 2 
  };

  // Get the plotProperties
  const plot = defaultPlots[device];
  const baseProps = devicePlots[device].plots[plot];
  const { minLevel: min, maxLevel: max, average: avg, deviation: std, roundTo = 1, target: tgt, units: legendUnits, labels: legendLabels } = baseProps;
  const areaArray = inContainer ? values(areas) : [areas[areaId]];
  const minLevel = isFinite(min) ? min : Math.min(...areaArray.map(obj => get(obj, min)).filter(Boolean));
  const maxLevel = isFinite(max) ? max : Math.max(...areaArray.map(obj => get(obj, max)).filter(Boolean));
  const useDistanceUnits = !legendUnits && !legendLabels;
  let labels = legendLabels;
  let units = legendUnits
  if (useDistanceUnits) {
    units = content.isMeter ? "Meters" : "Feet";
    const step = content.isMeter ? 1 : 3
    const distanceArray = Array.from({ length: Math.ceil(toFeet(maxLevel, content.isMeter) / step) }, (_, i) => i * step).slice(1);
    labels = distanceArray.reduce((acc, curr) => ({ ...acc, [toString(curr)]: toMeter(curr, content.isMeter) }), {})
  };
  const target = tgt ? Math.min(...areaArray.map(obj => get(obj, tgt)).filter(Boolean)) : minLevel * .35 + maxLevel * .65;
  let average = areaArray.map(obj => obj[avg]).filter(Boolean).reduce((avg, value, _, { length }) => avg + value / length, 0);
  average = average === 0 ? null : round(average, roundTo);
  let deviation = round(areaArray.map(obj => obj[std]).reduce((avg, value, _, { length }) => avg + value / length, 0), 2);
  deviation = isFinite(deviation) ? deviation : null;

  const plotProps = {
    ...baseProps,
    minLevel,
    maxLevel,
    labels,
    units,
    target,
    average,
    deviation,
  };
  const gradient = gradients[plotProps.plotType](plotProps);

  const newprops = {
    inContainer,
    scale,
    bbox,
    ...props
  };

  return (
    <Fragment>
      <View style={bbox} >
        <Canvas {...newprops} plotProps={plotProps} gradient={gradient}/>
        <FloorPlan {...newprops} />
      </View>
      <Scale plotProps={plotProps} gradient={gradient} />
    </Fragment>
  )
};

export default Plot;

